import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ALTabsComponent } from '@asamco/application-link/shared';
import { AsamcoUserService, Permissions } from '@asamco/base';
import { BehaviorSubject, merge, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { AdminWarningBannerComponent } from '../admin-tooling/tools/admin-warning-banner.component';

type UserManagementTabId = 'users' | 'groups';

type TabInfo = {
	id: UserManagementTabId;
	text: string;
	icon: string;
};

@Component({
	selector: 'app-shell-user-management-shell',
	templateUrl: './user-management-shell.component.html',
	styleUrls: ['./user-management-shell.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [AdminWarningBannerComponent, ALTabsComponent, AsyncPipe, NgIf, RouterOutlet],
})
export class UserManagementShellComponent implements OnInit {
	public readonly availableTabs$ = new BehaviorSubject<TabInfo[]>([]);
	public readonly selectedTabId$ = new BehaviorSubject<UserManagementTabId>('users');

	constructor(
		private activatedRoute: ActivatedRoute,
		private asamcoUserService: AsamcoUserService,
		private router: Router,
	) {}

	async ngOnInit(): Promise<void> {
		const [canViewUsers, canViewGroups] = await Promise.all([
			this.hasAssignedPermission('AS_UM_MI_USERS'),
			this.hasAssignedPermission('AS_UM_MI_GROUPS'),
		]);

		const availableTabs: TabInfo[] = [];
		if (canViewUsers) {
			availableTabs.push({ id: 'users', text: 'Users', icon: 'far fa-user' });
		}
		if (canViewGroups) {
			availableTabs.push({ id: 'groups', text: 'Groups', icon: 'far fa-users' });
		}

		this.availableTabs$.next(availableTabs);

		if (availableTabs.length === 0) {
			void this.router.navigateByUrl('/error/no-access');
			return;
		}

		merge(of(null), this.router.events.pipe(filter(event => event instanceof NavigationEnd)))
			.pipe(
				map(() => (this.activatedRoute.firstChild?.snapshot.url?.[0]?.path as UserManagementTabId | undefined) ?? availableTabs[0].id),
				startWith((this.activatedRoute.firstChild?.snapshot.url?.[0]?.path as UserManagementTabId | undefined) ?? availableTabs[0].id),
				distinctUntilChanged(),
			)
			.subscribe(tabId => this.selectedTabId$.next(tabId));

		if (!this.activatedRoute.firstChild) {
			void this.router.navigate([availableTabs[0].id], { relativeTo: this.activatedRoute });
		}
	}

	public readonly onTabClick = (tab: TabInfo) => {
		this.selectedTabId$.next(tab.id);
		void this.router.navigate([tab.id], { relativeTo: this.activatedRoute });
	};

	private async hasAssignedPermission(permissionKey: keyof typeof Permissions): Promise<boolean> {
		const permissionGuid = Permissions[permissionKey]?.toLowerCase();
		if (!permissionGuid) {
			return false;
		}

		const assignedPermissions = await this.asamcoUserService.getPermissions();
		return assignedPermissions.includes(permissionGuid);
	}
}
